<template>
  <div
    class="oliver-chat-panel"
    :style="{ '--top-bar-height': topBarHeight + 'px' }"
  >
    <!-- Content Area -->
    <div class="panel-content">
      <!-- Welcome View -->
      <welcome-view
        v-if="currentView === 'welcome'"
        :patient-mode-enabled="patientModeEnabled"
        :show-patient-mode="isOnPatientRoute"
        :patient-mode-btn-label="patientModeBtnLabel"
        :can-switch-patient="canSwitchToRoutePatient"
        :patient-capsule-label="patientCapsuleLabel"
        :web-search-enabled="webSearchEnabled"
        :is-fullscreen="isFullscreen"
        :is-customized="isCustomized"
        :is-wide-layout="isWideLayout"
        :is-grid-layout="isGridLayout"
        :is-docked="isDocked"
        :is-history-open="historySidebarOpen"
        @select-chat="handleSelectChat"
        @create-chat="handleCreateChat"
        @create-chat-with-message="handleCreateChatWithMessage"
        @show-history="handleShowHistory"
        @close-history="handleCloseHistory"
        @toggle-history="handleToggleHistory"
        @select-chat-from-history="handleSelectChatFromSidebar"
        @new-chat-from-history="handleNewChatFromSidebar"
        @toggle-nav-sidebar="navSidebarOpen = !navSidebarOpen"
        @close="$emit('close')"
        @toggle-fullscreen="$emit('toggle-fullscreen')"
        @reset-size="$emit('reset-size')"
        @toggle-dock="$emit('toggle-dock')"
        @start-popover-drag="handleHeaderDrag"
        @toggle-patient-mode="handlePatientModeToggle"
        @switch-patient-mode="handleSwitchToRoutePatient"
        @toggle-web-search="handleWebSearchToggle"
      />

      <!-- Chat View -->
      <chat-view
        v-else-if="currentView === 'chat'"
        :initial-message="initialMessage"
        :is-fullscreen="isFullscreen"
        :is-customized="isCustomized"
        :is-docked="isDocked"
        :is-wide="isWideLayout"
        :patient-mode-enabled="patientModeEnabled"
        :show-patient-mode="isOnPatientRoute || patientModeEnabled"
        :patient-mode-btn-label="patientModeBtnLabel"
        :can-switch-patient="canSwitchToRoutePatient"
        :patient-capsule-label="patientCapsuleLabel"
        :web-search-enabled="webSearchEnabled"
        :is-history-open="historySidebarOpen"
        @go-home="handleGoHome"
        @new-chat="handleCreateChat"
        @show-history="handleShowHistory"
        @close-history="handleCloseHistory"
        @toggle-history="handleToggleHistory"
        @select-chat-from-history="handleSelectChatFromSidebar"
        @new-chat-from-history="handleNewChatFromSidebar"
        @toggle-nav-sidebar="navSidebarOpen = !navSidebarOpen"
        @toggle-fullscreen="$emit('toggle-fullscreen')"
        @reset-size="$emit('reset-size')"
        @close="$emit('close')"
        @toggle-dock="$emit('toggle-dock')"
        @start-popover-drag="handleHeaderDrag"
        @toggle-patient-mode="handlePatientModeToggle"
        @switch-patient-mode="handleSwitchToRoutePatient"
        @toggle-web-search="handleWebSearchToggle"
      />
    </div>

    <!-- Navigation Sidebar — rendered after history so it stacks on top when both are open -->
    <navigation-sidebar
      :is-open="navSidebarOpen"
      :is-fullscreen="isFullscreen"
      @close="navSidebarOpen = false"
      @navigate="
        navSidebarOpen = false;
        $emit('close');
      "
    />
  </div>
</template>

<script>
import {
  OLIVER_CHAT_PARAM,
  readOliverChat,
  routeWithoutOliverChat,
} from "@/components/OliverBotV2/utils/oliverLinks.js";
import { NARROW_PANEL_WIDTH } from "@/components/OliverBotV2/utils/panelLayout.js";
import { isPatientRoute } from "@/components/OliverBotV2/utils/patientModeManager.js";
import ChatView from "@/components/OliverBotV2/views/ChatView.vue";
import NavigationSidebar from "@/components/OliverBotV2/views/welcome/NavigationSidebar.vue";
import WelcomeView from "@/components/OliverBotV2/views/welcome/WelcomeViewV2.vue";
import { mapActions, mapState } from "vuex";

export default {
  name: "OliverChatPanel",
  components: {
    WelcomeView,
    ChatView,
    NavigationSidebar,
  },
  props: {
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    isCustomized: {
      type: Boolean,
      default: false,
    },
    isWideLayout: {
      type: Boolean,
      default: false,
    },
    isGridLayout: {
      type: Boolean,
      default: false,
    },
    isDocked: {
      type: Boolean,
      default: false,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentView: "welcome", // 'welcome' | 'chat'
      initialMessage: null, // Message to send when creating new chat
      cardPatientActive: false, // true when patient mode was activated from welcome card
      patientModeEnabled: false,
      isOnPatientRoute: false,
      historySidebarOpen: false,
      historyClosedByUser: false,
      navSidebarOpen: false,
      panelWidth: Infinity,
    };
  },
  computed: {
    ...mapState({
      selectedChat: (state) => state.oliverChatBotV2.selectedChat,
      patient: (state) => state.patients.patient,
      patientMode: (state) => state.oliverChatBotV2.patientMode,
      authUser: (state) => state.auth.user,
      restoreRequest: (state) => state.oliverChatBotV2.restoreRequest,
      webSearchEnabled: (state) => state.oliverChatBotV2.webAccess,
    }),
    topBarHeight() {
      return this.isWideLayout && this.panelWidth >= 750 ? 56 : 48;
    },
    isNarrow() {
      return this.panelWidth < NARROW_PANEL_WIDTH;
    },
    selectedChatHistoryKnown() {
      return this.$store.getters["oliverChatBotV2/chatHistoryKnown"](
        this.selectedChat?.id
      );
    },
    selectedChatMessageCount() {
      const chatId = this.selectedChat?.id;
      if (!chatId) return 0;
      return (
        this.$store.state.oliverChatBotV2.chatStates[chatId]?.messages.length ||
        0
      );
    },
    // A restored tab was sent to one conversation, so the history list is not what it came
    // for — unlike opening Oliver yourself, where picking a chat is the point.
    suppressHistoryForRestore() {
      return this.restoreIsReady;
    },
    restoreIsReady() {
      return Boolean(
        this.restoreRequest?.status === "ready" && !this.restoreRequest.consumed
      );
    },
    // The conversation's client wins over the page, so this is the one-click way to move
    // it. Off means nothing to switch from — the Client Mode toggle covers that.
    canSwitchToRoutePatient() {
      return Boolean(
        this.isOnPatientRoute &&
          this.patient &&
          this.patientMode &&
          this.patient.id !== this.patientMode.id
      );
    },
    patientModeBtnLabel() {
      if (this.canSwitchToRoutePatient) {
        return `Switch to ${this.patient.first}?`;
      }
      return "Client Mode";
    },
    patientCapsuleLabel() {
      if (!this.patientModeEnabled || !this.patientMode) return null;
      return (
        `${this.patientMode.first || ""} ${
          this.patientMode.last || ""
        }`.trim() || null
      );
    },
  },
  watch: {
    selectedChat(newVal, oldVal) {
      if (newVal === null && this.currentView === "chat") {
        this.handleCreateChat();
      }
      if (newVal?.id !== oldVal?.id) this.resolvePatientMode();
    },
    // History landing is what settles a conversation's client mode.
    selectedChatMessageCount() {
      this.resolvePatientMode();
    },
    selectedChatHistoryKnown() {
      this.resolvePatientMode();
    },
    // A ready restore replaces Welcome, unless the user has moved on meanwhile.
    restoreRequest: {
      deep: true,
      immediate: true,
      handler(request) {
        if (request?.status === "failed") this.pruneStaleRestoreParam();
        if (!this.restoreIsReady) return;

        if (this.currentView !== "welcome") return;
        this.initialMessage = null;
        this.currentView = "chat";
      },
    },
    isOpen(val) {
      if (val && this.currentView === "welcome" && this.authUser) {
        this.$store.dispatch("appointments/getUpcoming", {
          userId: this.authUser.id,
          isProvider: this.authUser.isProvider,
          isAdmin: this.authUser.isAdmin,
          isManagingAdmin: this.authUser.isManagingAdmin,
        });
        this.$store.dispatch("timeline/getTimeline", {
          userId: this.authUser.id,
          size: 3,
        });
        this.$store.dispatch("patients/getWelcomeCardPatients", {
          userId: this.authUser.id,
        });
      }
      if (
        val &&
        this.isWideLayout &&
        !this.isNarrow &&
        !this.isDocked &&
        !this.historyClosedByUser &&
        !this.suppressHistoryForRestore
      ) {
        this.openHistory(false);
      }
    },
    $route: {
      handler(route) {
        this.isOnPatientRoute = isPatientRoute(route);
        // An invalid oliverChat param never becomes a request, so prune it here.
        this.pruneStaleRestoreParam();
      },
      immediate: true,
    },
    // The route only applies to a conversation with no client mode of its own; it never
    // overrides one that has.
    isOnPatientRoute: {
      immediate: true,
      handler(isOnPatient, wasOnPatient) {
        if (isOnPatient === wasOnPatient) return;
        this.resolvePatientMode();
      },
    },
    patient() {
      this.resolvePatientMode();
    },
    isDocked(val) {
      if (val) {
        this.closeHistory(false);
      }
    },
    async isWideLayout(newVal, oldVal) {
      if (
        this.isOpen &&
        newVal &&
        !oldVal &&
        !this.isNarrow &&
        !this.isDocked &&
        !this.historyClosedByUser
      ) {
        this.openHistory(false);
        if (this.currentView === "chat") {
          try {
            await this.getChats({ page: 1, limit: 50 });
          } catch (e) {
            // ignore non-fatal errors
          }
        }
      }
    },
    async currentView(newVal) {
      // Default to opening the history sidebar when entering chat on large screens
      if (
        newVal === "chat" &&
        this.panelWidth >= 750 &&
        !this.isDocked &&
        !this.historyClosedByUser &&
        !this.suppressHistoryForRestore
      ) {
        this.openHistory(false);
        try {
          await this.getChats({ page: 1, limit: 50 });
        } catch (e) {
          // ignore non-fatal errors
        }
      }
    },
    async panelWidth(newVal, oldVal) {
      // Only react when the panel CROSSES into the large layout — not on every
      // width change while already large. Otherwise each ResizeObserver tick
      // (layout settling, animations, opening history itself) re-opens history
      // and re-fetches the chat list, flooding /conversations. `Infinity` is the
      // initial sentinel, so the first real measurement counts as a crossing.
      if (
        newVal < NARROW_PANEL_WIDTH &&
        oldVal >= NARROW_PANEL_WIDTH &&
        this.historySidebarOpen
      ) {
        this.closeHistory(false);
      }
      const wasLarge = oldVal >= 750 && oldVal !== Infinity;
      const crossedToLarge = !wasLarge && newVal >= 750;
      if (
        crossedToLarge &&
        (this.currentView === "chat" || this.currentView === "welcome") &&
        !this.isDocked &&
        !this.historyClosedByUser
      ) {
        this.openHistory(false);
        if (this.currentView === "chat") {
          try {
            await this.getChats({ page: 1, limit: 50 });
          } catch (e) {
            // ignore non-fatal errors
          }
        }
      }
    },
  },
  mounted() {
    this._ro = new ResizeObserver(([entry]) => {
      // Debounce: coalesce rapid resize/animation ticks so panelWidth settles
      // once instead of thrashing the width-driven watcher.
      const width = entry.contentRect.width;
      clearTimeout(this._roTimer);
      this._roTimer = setTimeout(() => {
        this.panelWidth = width;
      }, 150);
    });
    this._ro.observe(this.$el);
  },
  beforeDestroy() {
    if (this._ro) this._ro.disconnect();
    clearTimeout(this._roTimer);
  },
  methods: {
    ...mapActions("oliverChatBotV2", [
      "setSelectedChat",
      "createChat",
      "getChats",
      "resetChatMessages",
      "setPatientMode",
      "consumeRestoreRequest",
      "setWebAccess",
    ]),
    ...mapActions("patients", ["setPatient"]),
    // The user picked a conversation for themselves — stop restoring and strip the param
    // (via `replace`, so Back doesn't return to the restored conversation).
    releaseRestoreContext() {
      this.consumeRestoreRequest();
      this.clearRestoreParam();
    },
    // Removes `oliverChat` from the URL, keeping path/hash/other params untouched.
    clearRestoreParam() {
      const to = routeWithoutOliverChat(this.$route);
      if (to) this.$router.replace(to).catch(() => {});
    },
    // Drops a restore param that can never work. A pending or succeeded one is left, so
    // restore survives a reload.
    pruneStaleRestoreParam() {
      if (this.$route.query?.[OLIVER_CHAT_PARAM] === undefined) return;
      const conversationId = readOliverChat(this.$route.query);
      const failed =
        this.restoreRequest?.conversationId === conversationId &&
        this.restoreRequest?.status === "failed";
      if (conversationId && !failed) return;
      this.clearRestoreParam();
    },
    // The composer's client mode: the conversation's own, else nothing while its history
    // is in flight, else the page's client. Safe to call at any point.
    // Synchronous on purpose: creating a conversation fires this several times in a row,
    // and an awaited one resuming late would overwrite a newer result.
    resolvePatientMode() {
      const chatId = this.selectedChat?.id || null;

      if (chatId && this.isOwnedChat(chatId)) {
        const patient =
          this.$store.getters["oliverChatBotV2/patientContextFor"](chatId) ||
          null;
        this.applyPatientMode(patient);
        return;
      }
      if (chatId && !this.isChatHistoryKnown(chatId)) {
        this.applyPatientMode(null);
        return;
      }
      this.applyPatientMode(this.isOnPatientRoute ? this.patient : null);
    },
    isOwnedChat(chatId) {
      return this.$store.getters["oliverChatBotV2/patientModeIsOwned"](chatId);
    },
    isChatHistoryKnown(chatId) {
      return this.$store.getters["oliverChatBotV2/chatHistoryKnown"](chatId);
    },

    // Transient, so the route can still correct it and nothing is left behind on switch.
    applyPatientMode(patient) {
      this.patientModeEnabled = Boolean(patient);
      this.setPatientMode(patient || null);
    },
    // A deliberate choice, recorded against the conversation so the route stops overriding it.
    writePatientMode(patient) {
      this.patientModeEnabled = Boolean(patient);
      this.$store.dispatch("oliverChatBotV2/setConversationPatientMode", {
        chatId: this.selectedChat?.id || null,
        patient: patient || null,
      });
    },
    handlePatientModeToggle(isEnabled) {
      this.cardPatientActive = false;
      if (!isEnabled) {
        this.writePatientMode(null);
        return;
      }
      // `state.patients.patient` can hold a client from an earlier visit, so only adopt
      // it while actually on that client's page.
      const routePatient = this.isOnPatientRoute ? this.patient : null;
      this.writePatientMode(this.patientMode || routePatient);
    },
    handleSwitchToRoutePatient() {
      this.cardPatientActive = false;
      this.writePatientMode(this.patient);
    },
    handleWebSearchToggle(isEnabled) {
      this.setWebAccess(isEnabled);
    },
    openHistory(isManual = true) {
      this.historySidebarOpen = true;
      if (isManual) {
        this.historyClosedByUser = false;
      }
    },
    closeHistory(isManual = true) {
      this.historySidebarOpen = false;
      if (isManual) {
        this.historyClosedByUser = true;
      }
    },
    handleShowHistory() {
      this.openHistory(true);
    },
    handleCloseHistory() {
      this.closeHistory(true);
    },
    handleToggleHistory() {
      if (this.historySidebarOpen) {
        this.closeHistory(true);
        return;
      }
      this.openHistory(true);
    },
    handleHeaderDrag(event) {
      if (!this.isFullscreen) {
        this.$emit("start-popover-drag", event);
      }
    },
    async handleSelectChat(chat) {
      this.releaseRestoreContext();
      this.initialMessage = null;
      await this.setSelectedChat(chat);
      this.currentView = "chat";
    },
    async handleCreateChat() {
      this.releaseRestoreContext();
      this.initialMessage = null;
      const response = await this.createChat();
      if (response && response.data) {
        await this.setSelectedChat(response.data.data);
        this.currentView = "chat";
      }
    },
    async handleCreateChatWithMessage(message) {
      this.releaseRestoreContext();
      this.initialMessage = message;
      if (message.patient && !this.isOnPatientRoute) {
        // Only update shared patient state when NOT on a patient profile page.
        // If we are on a patient route, the profile page owns state.patients.patient
        // and overwriting it would change which patient the profile displays.
        await this.setPatient(message.patient);
      }
      const response = await this.createChat();
      if (response && response.data) {
        await this.setSelectedChat(response.data.data);
        if (message.patient) {
          this.cardPatientActive = true;
          this.writePatientMode(message.patient);
        }
        this.currentView = "chat";
      }
    },
    handleGoHome() {
      this.releaseRestoreContext();
      const wasCardPatient = this.cardPatientActive;
      this.setSelectedChat(null);
      this.resetChatMessages();
      this.initialMessage = null;
      if (wasCardPatient && !this.isOnPatientRoute) {
        this.setPatient(null);
      }
      this.currentView = "welcome";
    },
    async handleSelectChatFromSidebar(chat) {
      await this.handleSelectChat(chat);
      if (!this.isWideLayout || this.isNarrow) {
        this.closeHistory(false);
      }
    },
    async handleNewChatFromSidebar() {
      this.closeHistory(false);
      await this.handleCreateChat();
    },
  },
};
</script>

<style lang="scss" scoped>
.oliver-chat-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
  position: relative;
}

.panel-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;

  &--fullscreen {
    cursor: default;
  }
}

.header-drag-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 80px;
  height: 100%;
  cursor: move;
  touch-action: none;
  z-index: 1;

  .panel-header--fullscreen & {
    cursor: default;
    pointer-events: none;
  }
}

.header-actions {
  display: flex;
  gap: 8px;
  position: relative;
  z-index: 2;
}

.icon-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(0.95);
  }

  i {
    pointer-events: none;
  }
}

.panel-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}
</style>
